/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IPaymentMethods } from '@msdyn365-commerce-modules/order-management';
import { PriceComponent } from '@msdyn365-commerce/components';
import { TenderLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
import { IPaymentMethodsInputExtended } from '../smwe-order-details';

const getLastFourDigit = (digits = '') => digits.substr(-4);

enum WalletTenderTypes {
    GOOGLEPAY = '15',
    APPLEPAY = '14'
}

const renderPaymentCard = (creditCardEndingLabel: string,
    cardLastFourDigit: string,
    googlePayLabel: string,
    applePayLabel: string,
    isValidCardNumber: boolean | string,
    cardTypeId?: string,
    tenderTypeId?: string
    ) => {
    switch (tenderTypeId) {
        case WalletTenderTypes.GOOGLEPAY:
            return googlePayLabel;

        case WalletTenderTypes.APPLEPAY:
            return applePayLabel;

        default:
            return `${cardTypeId} ${isValidCardNumber && `${creditCardEndingLabel} ${cardLastFourDigit}`}`;
    }
};

export const getOrderDetailsPaymentMethods = ({
    priceContext,
    tenderLines,
    tenderIdOperationIdMap,
    resource: { paymentMethodsTitle, creditCardEndingLabel, giftCardEndingLabel, amountCoveredLabel, loyaltyCardUsedLabel, cashUsedLabel },
    googlePayLabel,
    applePayLabel
}: IPaymentMethodsInputExtended): IPaymentMethods => {
    const getCard = (tenderLine: TenderLine, showAmount: boolean = false) => {
        const cardLastFourDigit = getLastFourDigit(tenderLine.MaskedCardNumber);

        // @ts-expect-error
        const isValidCardNumber = cardLastFourDigit && !isNaN(cardLastFourDigit);
        const amount = (tenderLine.AuthorizedAmount || 0) > 0 ? tenderLine.AuthorizedAmount : tenderLine.Amount;
        const showAmountCovered = !!(showAmount && amount && amount > 0);

        return (
            <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
                <p className='ms-order-details__payment-methods-card'>
                    {renderPaymentCard(creditCardEndingLabel, cardLastFourDigit, googlePayLabel, applePayLabel, isValidCardNumber, tenderLine.CardTypeId, tenderLine.TenderTypeId)}
                </p>
                {showAmountCovered && (
                    <p className='ms-order-details__payment-methods-card-amount-info'>
                        {amountCoveredLabel}
                        <PriceComponent
                            {...priceContext}
                            className='ms-order-details__payment-methods-card-amount'
                            data={{ price: { CustomerContextualPrice: amount || 0 } }}
                        />
                    </p>
                )}
            </div>
        );
    };

    const getGiftCertificate = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-gift-card-number'>
                {giftCardEndingLabel} {getLastFourDigit(tenderLine.GiftCardId)}
            </p>
            <p className='ms-order-details__payment-methods-gift-card-amount-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-gift-card-amount'
                    data={{price: { CustomerContextualPrice: tenderLine.Amount || 0 }}}
                />
            </p>
        </div>
    );

    const getLoyalty = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-loyalty-number'>
                {loyaltyCardUsedLabel} {tenderLine.LoyaltyCardId}
            </p>
            <p className='ms-order-details__payment-methods-loyalty-amount-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-loyalty-amount'
                    data={{ price: { CustomerContextualPrice: tenderLine.Amount || 0 }}}
                />
            </p>
        </div>
    );

    const getCash = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-cash'>{cashUsedLabel}</p>
            <p className='ms-order-details__payment-methods-cash-amount-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-cash-amount'
                    data={{ price: {CustomerContextualPrice: tenderLine.Amount || 0 }}}
                />
            </p>
        </div>
    );

    return {
        paymentMethodsProps: { className: 'ms-order-details__payment-methods' },
        title: <div role='heading' aria-level={2} className='ms-order-details__payment-methods-title'>{paymentMethodsTitle}</div>,
        methods: tenderLines.reduce((lines: React.ReactNode[], tenderLine) => {
            if (!tenderLine.TenderTypeId) {
                return lines;
            }
            switch (tenderIdOperationIdMap[tenderLine.TenderTypeId]) {
                case 201:
                    lines.push(getCard(tenderLine));
                    break;
                case 214:
                    lines.push(getGiftCertificate(tenderLine));
                    break;
                case 207:
                    lines.push(getLoyalty(tenderLine));
                    break;
                case 200:
                    lines.push(getCash(tenderLine));
                    break;
                default:
                // do nothing
            }
            return lines;
        },                          [])
    };
};
